import React, { useState, useEffect } from 'react';
import BackendPage from '../../components/page/BackendPage';
import Content from '../../components/Content';
import { Tabs } from '@mantine/core';
import { IconUsers, IconTicket, IconStethoscope } from '@tabler/icons-react';
import SubscriptionsReport from '../../components/reports/SubscriptionsReport';
import DateTimeUtils, { DATE_FORMAT } from '@dr-pam/common-components/Utils/DateTimeUtils';
import ProductPurchasesReport from '../../components/reports/ProductPurchasesReport';
import CouponReport from '../../components/reports/CouponReport';
import HealthProfessionalReport from '../../components/reports/HealthProfessionalReport';
import { useSearchParams } from 'react-router-dom';

type ReportTab = 'subscription-report' | 'product-purchases-report' | 'health-professional-report' | 'coupon-report';

export default function ReportPage() {
	const [searchParams] = useSearchParams();
	const [selectedTab, setSelectedTab] = useState<ReportTab>('subscription-report');
	const [preselectedCouponId, setPreselectedCouponId] = useState<string | undefined>(undefined);

	useEffect(() => {
		const tabParam = searchParams.get('tab');
		const couponIdParam = searchParams.get('couponId');

		if (tabParam === 'coupon-report') {
			setSelectedTab('coupon-report');
			setPreselectedCouponId(couponIdParam || undefined);
		} else {
			setPreselectedCouponId(undefined);
		}
	}, [searchParams]);

	const handleTabChanged = (tab: string | null) => {
		if (!tab) {
			return;
		}
		setSelectedTab(tab as ReportTab);
	};

	const handleReportGenerated = (report: Blob, filenamePrefix: string) => {
		const url = URL.createObjectURL(report);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${filenamePrefix}_${DateTimeUtils.format(new Date(), DATE_FORMAT)}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<BackendPage title="Reports" centerChildren>
			<Content fillHeight>
				<Tabs value={selectedTab} onChange={handleTabChanged}>
					<Tabs.List mb="md">
						<Tabs.Tab value="subscription-report" leftSection={<IconUsers />}>
							Subscription Report
						</Tabs.Tab>
						<Tabs.Tab value="product-purchases-report" leftSection={<IconUsers />}>
							Product Purchases Report
						</Tabs.Tab>
						<Tabs.Tab value="coupon-report" leftSection={<IconTicket />}>
							Coupon Report
						</Tabs.Tab>
						<Tabs.Tab value="health-professional-report" leftSection={<IconStethoscope />}>
							Health Professional Report
						</Tabs.Tab>
					</Tabs.List>
				</Tabs>
				{selectedTab === 'subscription-report' && (
					<>
						<SubscriptionsReport onReportGenerated={handleReportGenerated} />
					</>
				)}
				{selectedTab === 'product-purchases-report' && (
					<ProductPurchasesReport onReportGenerated={handleReportGenerated} />
				)}
				{selectedTab === 'coupon-report' && (
					<CouponReport onReportGenerated={handleReportGenerated} preselectedCouponId={preselectedCouponId} />
				)}
				{selectedTab === 'health-professional-report' && (
					<HealthProfessionalReport onReportGenerated={handleReportGenerated} />
				)}
			</Content>
		</BackendPage>
	);
}
